import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Package } from 'lucide-react';
import { productosService, categoriasService, marcasService, unidadesService } from '@/services/productos.service';
import { satCatalogService } from '@/services/sat-catalog.service';
import { useAuthStore } from '@/stores/auth-store';
import { useApiError } from '@/hooks/useApiError';
import api from '@/lib/axios';

export function ProductFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { handleAuthRedirect } = useApiError();
  const isEditing = !!id;
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Dynamic select data
  const [categories, setCategories] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [taxes, setTaxes] = useState<any[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  // SAT catalog autocomplete
  const [satResults, setSatResults] = useState<any[]>([]);
  const [showSatDropdown, setShowSatDropdown] = useState(false);
  const [satSearchLoading, setSatSearchLoading] = useState(false);
  const [satInputValue, setSatInputValue] = useState('');
  const satContainerRef = useRef<HTMLDivElement>(null);
  const satInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<any>(null);
  const satCatalogFullRef = useRef<any[]>([]);

  const [form, setForm] = useState({
    name: '',
    sku: '',
    barcode: '',
    description: '',
    categoryId: '',
    brandId: '',
    unitId: '',
    pricePublic: '',
    priceWholesale: '',
    wholesaleMinQuantity: '',
    priceDistributor: '',
    priceVip: '',
    costAverage: '',
    stock: '0',
    minStock: '0',
    maxStock: '',
    satKey: '',
    isService: false,
    pricesIncludeTax: true,
    taxIds: [] as string[],
  });

  // Load catalogs for selects
  useEffect(() => {
    const loadCatalogs = async () => {
      setLoadingCatalogs(true);
      try {
        const companyId = user?.company?.id;
        if (!companyId) return;
        const [cats, brds, unts, txs] = await Promise.all([
          categoriasService.findAll(companyId),
          marcasService.findAll(companyId),
          unidadesService.findAll(companyId),
          api.get(`/taxes/company/${companyId}`).then(res => res.data),
        ]);
        setCategories(cats || []);
        setBrands(brds || []);
        setUnits(unts || []);
        setTaxes(txs || []);
      } catch {
        // Silently fail, selects will show empty
      } finally {
        setLoadingCatalogs(false);
      }
    };
    loadCatalogs();
  }, [user?.company?.id]);

  // Load SAT catalog on mount (full list for initial suggestions)
  useEffect(() => {
    satCatalogService.findProducts('').then(data => {
      const list = data || [];
      satCatalogFullRef.current = list;
      setSatResults(list);
      // Set input value to show the description for editing
      if (form.satKey) {
        const found = list.find((s: any) => s.code === form.satKey);
        if (found) setSatInputValue(`${found.code} - ${found.name}`);
      }
    }).catch(() => {});
  }, []);

  // Debounced SAT search for autocomplete
  const searchSatProducts = useCallback(async (search: string) => {
    setSatSearchLoading(true);
    try {
      const result = await satCatalogService.findProducts(search || '');
      setSatResults(result || []);
    } catch { /* empty */ }
    setSatSearchLoading(false);
  }, []);

  const handleSatInputChange = (value: string) => {
    setSatInputValue(value);
    setShowSatDropdown(true);
    // Store raw code if user types a code directly
    if (/^\d{8}$/.test(value.trim())) {
      setForm((prev) => ({ ...prev, satKey: value.trim() }));
    } else if (value === '') {
      setForm((prev) => ({ ...prev, satKey: '' }));
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchSatProducts(value), 200);
  };

  const selectSatProduct = (code: string, display: string) => {
    setForm((prev) => ({ ...prev, satKey: code }));
    setSatInputValue(display);
    setShowSatDropdown(false);
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (satContainerRef.current && !satContainerRef.current.contains(e.target as Node)) {
        setShowSatDropdown(false);
        // Restore display using full catalog (not filtered results)
        if (form.satKey && satCatalogFullRef.current.length > 0) {
          const found = satCatalogFullRef.current.find((s: any) => s.code === form.satKey);
          if (found) setSatInputValue(`${found.code} - ${found.name}`);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [form.satKey]);

  // When product loads in editing mode, update the input display
  // Depend on satResults too in case catalog loads after product (race condition)
  useEffect(() => {
    if (form.satKey && satCatalogFullRef.current.length > 0 && !satInputValue.includes(form.satKey)) {
      const found = satCatalogFullRef.current.find((s: any) => s.code === form.satKey);
      if (found) setSatInputValue(`${found.code} - ${found.name}`);
    }
  }, [form.satKey, satResults]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  useEffect(() => {
    if (isEditing && id) {
      loadProduct(id);
    }
  }, [id]);

  const loadProduct = async (productId: string) => {
    setLoading(true);
    try {
      const product = await productosService.findById(productId);
      setForm({
        name: product.name || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        description: product.description || '',
        categoryId: product.categoryId || '',
        brandId: product.brandId || '',
        unitId: product.unitId || '',
        pricePublic: product.pricePublic?.toString() || '',
        priceWholesale: product.priceWholesale?.toString() || '',
        wholesaleMinQuantity: product.wholesaleMinQuantity?.toString() || '',
        priceDistributor: product.priceDistributor?.toString() || '',
        priceVip: product.priceVip?.toString() || '',
        costAverage: product.costAverage?.toString() || '',
        stock: product.stock?.toString() || '0',
        minStock: product.minStock?.toString() || '0',
        maxStock: product.maxStock?.toString() || '',
        satKey: product.satKey || '',
        isService: product.isService || false,
        pricesIncludeTax: product.pricesIncludeTax ?? true,
        taxIds: product.productTaxes ? product.productTaxes.map((pt: any) => pt.taxId) : [],
      });
    } catch (err: any) {
      if (err?.response?.status === 401) { handleAuthRedirect(); return; }
      setError(err.response?.data?.message || 'Error al cargar producto');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleManualChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const data = {
        companyId: user?.company?.id,
        sku: form.sku,
        name: form.name,
        barcode: form.barcode || undefined,
        description: form.description || undefined,
        categoryId: form.categoryId || undefined,
        brandId: form.brandId || undefined,
        unitId: form.unitId,
        satKey: form.satKey || undefined,
        pricePublic: parseFloat(form.pricePublic) || 0,
        priceWholesale: form.priceWholesale ? parseFloat(form.priceWholesale) : undefined,
        wholesaleMinQuantity: form.wholesaleMinQuantity ? parseInt(form.wholesaleMinQuantity) : undefined,
        priceDistributor: form.priceDistributor ? parseFloat(form.priceDistributor) : undefined,
        priceVip: form.priceVip ? parseFloat(form.priceVip) : undefined,
        costAverage: form.costAverage ? parseFloat(form.costAverage) : undefined,
        stock: parseInt(form.stock) || 0,
        minStock: parseInt(form.minStock) || 0,
        maxStock: form.maxStock ? parseInt(form.maxStock) : undefined,
        isService: form.isService,
        pricesIncludeTax: form.pricesIncludeTax,
        taxIds: form.taxIds,
      };

      if (isEditing && id) {
        await productosService.update(id, data);
      } else {
        await productosService.create(data);
      }
      navigate('/products');
    } catch (err: any) {
      if (err?.response?.status === 401) { handleAuthRedirect(); return; }
      setError(err.response?.data?.message || 'Error al guardar producto');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/products')} className="p-2 rounded-lg hover:bg-ios-gray-6 transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold">{isEditing ? 'Editar Producto' : 'Nuevo Producto'}</h1>
            <p className="text-sm text-ios-gray mt-1">{isEditing ? 'Modifica los datos del producto' : 'Agrega un nuevo producto al catálogo'}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-ios-red text-sm p-4 rounded-[12px] border border-red-100">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="ios-card p-6 space-y-4">
          <h2 className="font-semibold text-base flex items-center gap-2">
            <Package size={18} className="text-ios-blue" />
            Información del Producto
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium mb-1.5 block">Nombre del producto *</label>
              <input type="text" name="name" value={form.name} onChange={handleChange} className="ios-input w-full" placeholder="Ej: Sabritas Original 42g" required />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">SKU *</label>
              <input type="text" name="sku" value={form.sku} onChange={handleChange} className="ios-input w-full" placeholder="Ej: SAB-001" required />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Código de barras</label>
              <input type="text" name="barcode" value={form.barcode} onChange={handleChange} className="ios-input w-full" placeholder="Ej: 750100123456" />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium mb-1.5 block">Descripción</label>
              <textarea name="description" value={form.description} onChange={handleChange} className="ios-input w-full min-h-[80px] resize-none" placeholder="Descripción del producto..." />
            </div>
          </div>
        </div>

        <div className="ios-card p-6 space-y-4">
          <h2 className="font-semibold text-base">Clasificación</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Categoría</label>
              <select className="ios-input w-full" name="categoryId" value={form.categoryId} onChange={handleChange} disabled={loadingCatalogs}>
                <option value="">
                  {loadingCatalogs ? 'Cargando...' : 'Seleccionar...'}
                </option>
                {categories.map((cat: any) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Marca</label>
              <select className="ios-input w-full" name="brandId" value={form.brandId} onChange={handleChange} disabled={loadingCatalogs}>
                <option value="">
                  {loadingCatalogs ? 'Cargando...' : 'Seleccionar...'}
                </option>
                {brands.map((b: any) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Unidad *</label>
              <select className="ios-input w-full" name="unitId" value={form.unitId} onChange={handleChange} required disabled={loadingCatalogs}>
                <option value="">
                  {loadingCatalogs ? 'Cargando...' : 'Seleccionar...'}
                </option>
                {units.map((u: any) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.abbreviation})</option>
                ))}
              </select>
            </div>
            <div ref={satContainerRef} className="relative">
              <label className="text-sm font-medium mb-1.5 block">Clave SAT (CFDI 4.0)</label>
              <div className="relative">
                <input
                  ref={satInputRef}
                  type="text"
                  value={satInputValue}
                  onChange={(e) => handleSatInputChange(e.target.value)}
                  onFocus={() => {
                    setShowSatDropdown(true);
                    if (satResults.length === 0) searchSatProducts('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setShowSatDropdown(false);
                  }}
                  className="ios-input w-full pr-8"
                  placeholder="Escribe para buscar (ej: botanas, pan, leche)..."
                  autoComplete="off"
                />
                {form.satKey && satInputValue && (
                  <button
                    type="button"
                    onClick={() => {
                      setForm(p => ({ ...p, satKey: '' }));
                      setSatInputValue('');
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-ios-gray hover:text-ios-red text-lg leading-none"
                  >
                    ×
                  </button>
                )}
              </div>
              {/* Autocomplete dropdown */}
              {showSatDropdown && (
                <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-ios-gray-5 rounded-[12px] shadow-ios-lg max-h-56 overflow-y-auto">
                  {satSearchLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="w-5 h-5 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin" />
                    </div>
                  ) : satResults.length === 0 ? (
                    <div className="text-center py-6 text-ios-gray text-sm">
                      No se encontraron resultados
                    </div>
                  ) : (
                    satResults.map((item: any) => (
                      <button
                        key={item.code}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectSatProduct(item.code, `${item.code} - ${item.name}`);
                        }}
                        className={`w-full text-left px-3 py-2.5 text-sm hover:bg-ios-blue/5 transition-colors border-b border-ios-gray-5 last:border-b-0 ${
                          form.satKey === item.code ? 'bg-ios-blue/10 text-ios-blue font-medium' : ''
                        }`}
                      >
                        <code className="text-xs font-mono bg-ios-gray-6 px-1.5 py-0.5 rounded mr-2">{item.code}</code>
                        <span>{item.name}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className={`ios-toggle ${form.isService ? 'active' : 'inactive'}`} onClick={() => handleManualChange('isService', !form.isService)}>
                  <span className="ios-toggle-thumb" />
                </div>
                <span className="text-sm font-medium">Es servicio</span>
              </label>
            </div>
          </div>
        </div>

        <div className="ios-card p-6 space-y-4">
          <h2 className="font-semibold text-base">Precios</h2>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Público *</label>
              <input type="number" step="0.01" name="pricePublic" value={form.pricePublic} onChange={handleChange} className="ios-input w-full" placeholder="0.00" required />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Precio Mayoreo</label>
              <input type="number" step="0.01" name="priceWholesale" value={form.priceWholesale} onChange={handleChange} className="ios-input w-full" placeholder="0.00" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Cant. Min. Mayoreo</label>
              <input type="number" name="wholesaleMinQuantity" value={form.wholesaleMinQuantity} onChange={handleChange} className="ios-input w-full" placeholder="Ej: 10" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Distribuidor</label>
              <input type="number" step="0.01" name="priceDistributor" value={form.priceDistributor} onChange={handleChange} className="ios-input w-full" placeholder="0.00" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">VIP</label>
              <input type="number" step="0.01" name="priceVip" value={form.priceVip} onChange={handleChange} className="ios-input w-full" placeholder="0.00" />
            </div>
          </div>
        </div>

        <div className="ios-card p-6 space-y-4">
          <h2 className="font-semibold text-base">Impuestos</h2>
          <div className="flex flex-col gap-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <div className={`ios-toggle ${form.pricesIncludeTax ? 'active' : 'inactive'}`} onClick={() => handleManualChange('pricesIncludeTax', !form.pricesIncludeTax)}>
                <span className="ios-toggle-thumb" />
              </div>
              <div>
                <span className="text-sm font-medium block">Los precios ya incluyen impuestos</span>
                <span className="text-xs text-ios-gray">Si está inactivo, los impuestos se sumarán al total en el Punto de Venta</span>
              </div>
            </label>
            
            <div>
              <label className="text-sm font-medium mb-3 block">Impuestos aplicables a este producto</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {taxes.map((tax: any) => (
                  <label key={tax.id} className="flex items-center gap-3 p-3 bg-ios-gray-6 rounded-xl cursor-pointer border border-transparent hover:border-ios-blue transition-colors">
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded border-gray-300 text-ios-blue focus:ring-ios-blue"
                      checked={form.taxIds.includes(tax.id)}
                      onChange={(e) => {
                        const newTaxIds = e.target.checked 
                          ? [...form.taxIds, tax.id]
                          : form.taxIds.filter(id => id !== tax.id);
                        handleManualChange('taxIds', newTaxIds);
                      }}
                    />
                    <div>
                      <span className="text-sm font-medium text-ios-dark block">{tax.name}</span>
                      <span className="text-xs text-ios-gray">{tax.factor} - {Number(tax.rate * 100).toFixed(0)}%</span>
                    </div>
                  </label>
                ))}
                {taxes.length === 0 && !loadingCatalogs && (
                  <div className="col-span-full text-sm text-ios-gray py-2">
                    No hay impuestos configurados en el sistema. Ve a Configuración &gt; Impuestos.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="ios-card p-6 space-y-4">
          <h2 className="font-semibold text-base">Inventario</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Stock actual</label>
              <input type="number" name="stock" value={form.stock} onChange={handleChange} className="ios-input w-full" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Stock mínimo</label>
              <input type="number" name="minStock" value={form.minStock} onChange={handleChange} className="ios-input w-full" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Stock máximo</label>
              <input type="number" name="maxStock" value={form.maxStock} onChange={handleChange} className="ios-input w-full" />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="ios-button flex items-center gap-2">
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Guardando...</>
            ) : (
              <><Save size={18} /> Guardar</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
